import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import {
  endOfDay,
  format,
  isAfter,
  isValid,
  isWithinInterval,
  startOfDay,
  sub,
} from 'date-fns';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { uniqBy } from 'lodash';
import { useInView } from 'react-intersection-observer';
import { useHistory } from 'react-router-dom';

import { PatientListModesMap } from '../../@types/appointments';
import { PdfViewerV2 } from '../../common-components/composed/pdfViewerV2/PdfViewerV2';
import type {
  Appointment,
  AppointmentProvider,
} from '../../react-query/useAppointments';
import { useAppointments } from '../../react-query/useAppointments';
import { useFmxSummaries } from '../../react-query/useFmxSummaries';
import { useGetTasks } from '../../react-query/useGetTasks';
import { useGetZuubInsuranceList } from '../../react-query/useGetZuubInsuranceList';
import { usePreferences } from '../../react-query/usePreferences';
import type { TTreatmentCounts } from '../../react-query/useTreatmentCounts';
import { useTreatmentCounts } from '../../react-query/useTreatmentCounts';
import type {
  TVisitSummary,
  TVisitSummaryArgs,
} from '../../react-query/useVisitSummaries';
import { useVisitSummaries } from '../../react-query/useVisitSummaries';
import { useCornerstoneInitialization } from '../../utils/cornerstone/cornerstoneAtoms';
import { useAppContext } from '../../utils/hooks/useAppContext';
import { useDebounce } from '../../utils/hooks/useDebounce';
import { useSearchParams } from '../../utils/hooks/useSearchParams';
import { mixpanelEvents, useTrackEvent } from '../../utils/mixpanel';
import {
  FeatureFlagType,
  PracticeFeatureFlagKey,
  useFeatureFlag,
} from '../../utils/user';
import { ClaimSummary } from '../claims/freeClaims/ClaimSummary';

import {
  INITIAL_SELECTED_ANALYSES_STATE,
  hasNewAnalysesAtom,
  jumpToCurrentApptAtom,
  modeAtom,
  operatoryListAtom,
  patientListViewAtom,
  searchAtom,
  selectDateAtom,
  selectUserExperienceAtom,
  selectedAnalysesAtom,
  sortFilterAtom,
  unifiedIdAtom,
  vaultAppointmentsAtom,
} from './atoms';
import {
  buildZuubInsuranceByUnifiedId,
  createLocalDate,
  findNewestAnalyses,
  getSortParam,
  useAppointmentsFilteredByProviderAndOperatory,
  useAppointmentsFilteredByTasksRecommendations,
  useIsScheduleViewEnabled,
} from './helpers';
import { PatientListPageLayout } from './PatientListPageLayout';
import { PatientStrip } from './patientStrip/PatientStrip';
import PatientInfoSheet from './patientStrip/patientStripRedesigned/patientInfoSheet/PatientInfoSheet';
import * as Styled from './styles';
import { PatientListPageLayoutContextMap } from './types';
import { getNextAppointment } from './useScrollAppointmentIntoView';
import { useShouldHideHuddle } from './useShouldHideHuddle';

export const PatientList = () => {
  // refs
  const parentRef = useRef<HTMLDivElement>(null);
  const lastViewedAppointmentRef = useRef<HTMLDivElement | null>(null);

  // global state
  const jumpToCurrentAppt = useAtomValue(jumpToCurrentApptAtom);
  const mode = useAtomValue(modeAtom);
  const search = useAtomValue(searchAtom);
  const selectedUserExperience = useAtomValue(selectUserExperienceAtom);
  const sortFilter = useAtomValue(sortFilterAtom);
  const unifiedId = useAtomValue(unifiedIdAtom);
  const isVaultAppointmentsEnabled = useAtomValue(vaultAppointmentsAtom);

  const setOperatoryList = useSetAtom(operatoryListAtom);
  const setSelectedAnalysesAtom = useSetAtom(selectedAnalysesAtom);

  const [hasNewAnalyses, setHasNewAnalyses] = useAtom(hasNewAnalysesAtom);
  const [selectedDate, selectDate] = useAtom(selectDateAtom);
  const [selectedTab, setSelectedTab] = useAtom(patientListViewAtom);

  // local state
  const [isRefreshRequested, setIsRefreshRequested] = useState(false);

  const [latestAnalysisDate, setLatestAnalysisDate] = useState<string | null>(
    null,
  );
  const [newestUnifiedId, setNewestUnifiedId] = useState<string | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<{
    unifiedId: string;
    estimatedVisitId?: number;
  }>({
    unifiedId: '',
    estimatedVisitId: 1,
  });

  // hooks
  const history = useHistory();
  const isScheduleViewEnabled = useIsScheduleViewEnabled();
  const isVideaDemoUser = !!useFeatureFlag(PracticeFeatureFlagKey.IS_SALES_DEMO)
    ?.value;
  const isSalesDemo = !!useFeatureFlag(PracticeFeatureFlagKey.IS_SALES_DEMO)
    ?.value;
  const isDualModeEnabled = !!useFeatureFlag(
    PracticeFeatureFlagKey.DUAL_MODE_ENABLED,
    FeatureFlagType.PRACTICE,
  )?.value;
  const shouldHideHuddle = useShouldHideHuddle();
  const { user } = useAppContext();
  const { data: preferences, isLoading: isLoadingPreferences } =
    usePreferences();
  const isHuddleDisabled = preferences?.huddleDisabled;
  const { trackEvent } = useTrackEvent();
  const { debouncedValue: debouncedSearch } = useDebounce(search);
  const searchParams = useSearchParams();
  const { data: prefs } = usePreferences();
  const { ref: bottomRef, inView: isBottomAppointmentInView } = useInView();
  const { inView: isNextAppointmentInView } = useInView();
  const { inView: isNewAnalysisInView } = useInView();

  const today = useMemo(() => {
    if (isVideaDemoUser) return new Date('2023-09-20');
    return new Date();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useMemo has a missing dependency: 'isVideaDemoUser'. Either include it or remove the dependency array.
  }, []);

  // TODO: Can we delete this since we no longer render x-rays in the context of huddle?
  useCornerstoneInitialization();

  const isSearchValid = debouncedSearch.length > 2;

  let endDate: Date | undefined = endOfDay(today);
  let startDate: Date | undefined = undefined;
  let imagingStartDate: Date | undefined;
  let imagingEndDate: Date | undefined;
  if (selectedDate) {
    endDate = endOfDay(selectedDate);
  }
  if (isSearchValid) {
    endDate = undefined;
  }
  if (selectedDate) {
    startDate = startOfDay(selectedDate);
  }
  if (sortFilter === "Today's Radiographs") {
    imagingStartDate = startOfDay(today);
    imagingEndDate = endOfDay(today);
    startDate = startOfDay(today);
  }

  const {
    data: appointmentData,
    isLoading: isLoadingAppointments,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useAppointments({
    appointmentOpts: {
      startDate,
      endDate,
      imagingStartDate,
      imagingEndDate,
      onlyImaging:
        isHuddleDisabled || (mode === 'RECENT_VISITS' && !isSearchValid),
      selectedView:
        !isDualModeEnabled ||
        (!!selectedDate &&
          isAfter(selectedDate, new Date()) &&
          selectedTab === 'HUDDLE')
          ? undefined
          : selectedUserExperience,
    },
    patientOpts: {
      name: isSearchValid ? debouncedSearch : undefined,
    },
    meta: {
      sort: {
        appointmentOn: getSortParam(mode, sortFilter),
      },
      pagination: {
        limit:
          (mode === PatientListModesMap.DailyHuddle ||
            sortFilter === "Today's Radiographs") &&
          !isVaultAppointmentsEnabled
            ? undefined
            : 10,
      },
    },
  });

  const practiceName = user?.primaryPracticeName || '';
  const canJumpToNewAnalyses = hasNewAnalyses && !isNewAnalysisInView;

  const estimatedVisitIds = appointmentData?.pages?.map((page) =>
    page.data?.map((appointment) => ({
      estimatedVisitId: appointment.appointment.estimatedVisitId,
    })),
  );

  const unifiedAppointmentIds = appointmentData?.pages.map((page) =>
    page.data.map((appointment) => ({
      unifiedAppointmentId: appointment.appointment.unifiedId,
    })),
  );
  const treatmentCountsData = useTreatmentCounts(unifiedAppointmentIds);
  const treatmentCountsPages = treatmentCountsData?.map((countData) => {
    if (Array.isArray(countData?.data?.counts)) {
      return countData?.data?.counts.map((data) => data);
    }
  });
  const bulkTreatmentCounts = treatmentCountsPages?.flat();
  const fmxSummaryData = useFmxSummaries(estimatedVisitIds);
  const fmxSummariesPages = fmxSummaryData?.map((summaryData) => {
    if (Array.isArray(summaryData?.data)) {
      return summaryData?.data?.map((data) => data);
    }
  });
  const fmxSummaries = fmxSummariesPages?.flat();
  const visitsArgs: TVisitSummaryArgs = {
    visits: appointmentData?.pages?.flatMap((appointmentResponse) =>
      appointmentResponse.data
        .map((appointment) => ({
          unifiedAppointmentId: appointment.appointment.unifiedId
            ? Number(appointment.appointment.unifiedId)
            : undefined,
          estimatedVisitId: appointment.appointment.estimatedVisitId,
        }))
        .flat(),
    ),
  };
  const visitSummariesData = useVisitSummaries(visitsArgs);
  const visitSummariesPages = visitSummariesData?.map((visitSummaryData) => {
    if (Array.isArray(visitSummaryData?.data)) {
      return visitSummaryData?.data?.map((data) => data);
    }
  });
  const visitSummaries = visitSummariesPages
    ?.flat()
    .filter(Boolean) as TVisitSummary[];

  const visitSummariesDeduplicated = useMemo(() => {
    const seen = new Set<string>();

    return visitSummaries.filter((summary) => {
      // Create a unique key combining both IDs
      const key = `${summary.unifiedAppointmentId}-${
        summary.estimatedVisitId ?? 'null'
      }`;

      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [visitSummaries]);

  const unifiedAppointmentIdsByPage = (appointmentData?.pages ?? []).map(
    (page) => (page.data ?? []).map(({ appointment }) => appointment.unifiedId),
  );

  const { appointmentIdsToTasks, getTaskCounts } = useGetTasks({
    unifiedAppointmentIdsByPage,
  });

  // Get metadata from last appointment page
  const { nextCursor } =
    appointmentData?.pages[appointmentData.pages.length - 1] || {};

  const allAppointments = useMemo(() => {
    // Anytime appointments change reset thumbnail state
    setSelectedAnalysesAtom(INITIAL_SELECTED_ANALYSES_STATE);
    const { hasNewerAnalyses, newerUnifiedId, updatedAnalysisDate } =
      findNewestAnalyses(appointmentData, latestAnalysisDate);
    if (hasNewerAnalyses && newerUnifiedId) {
      setHasNewAnalyses(true);
      setLatestAnalysisDate(updatedAnalysisDate);
      setNewestUnifiedId(newerUnifiedId);
    }
    return appointmentData?.pages.flatMap((page) => page.data);
  }, [
    setSelectedAnalysesAtom,
    appointmentData,
    latestAnalysisDate,
    setHasNewAnalyses,
  ]);

  const appointmentsFilteredByProviderAndOperatory =
    useAppointmentsFilteredByProviderAndOperatory(allAppointments);

  const appointments = useAppointmentsFilteredByTasksRecommendations(
    appointmentsFilteredByProviderAndOperatory,
    appointmentIdsToTasks,
    prefs,
    visitSummariesDeduplicated,
  );

  const unifiedIdsForZuub = useMemo(
    () => appointments.map((a) => a.appointment.unifiedId),
    [appointments],
  );
  const { data: zuubData } = useGetZuubInsuranceList({
    unifiedIds: unifiedIdsForZuub,
    enabled:
      !!prefs?.isInsuranceEligibilityEnabled && unifiedIdsForZuub.length > 0,
  });
  const zuubInsuranceByAppointmentId = useMemo(
    () => buildZuubInsuranceByUnifiedId(zuubData, appointments),
    [zuubData, appointments],
  );

  const rowVirtualizer = useVirtualizer({
    count: appointments?.length ?? 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => {
      let estimateSize = 170;
      if (selectedTab === 'REVIEW') {
        estimateSize = 143;
      }
      return estimateSize;
    },
  });

  const nextAppointmentId = getNextAppointment({ appointments, selectedDate });
  const taskCounts = getTaskCounts(appointmentsFilteredByProviderAndOperatory);

  const providersList = useMemo(() => {
    const providers = allAppointments
      ?.map(({ appointment }) => appointment.provider)
      .filter((a) => !!a);
    return uniqBy(providers, 'id') as AppointmentProvider[];
  }, [allAppointments]);

  const patientListContainerClasses = classNames({
    'patient-list': true,
    'patient-grid-view': selectedTab === 'MICRO',
  });

  const patientListClasses = classNames({
    'patient-list-content': true,
    'grid-view': selectedTab === 'MICRO',
  });

  // Calculate indices using useMemo
  const { nextAppointmentIndex, newAnalysisIndex, lastViewedAppointmentIndex } =
    useMemo(() => {
      return {
        nextAppointmentIndex: appointments.findIndex(
          (appointment) =>
            appointment.appointment.unifiedId === nextAppointmentId,
        ),
        newAnalysisIndex: appointments.findIndex(
          (appointment) =>
            appointment.appointment.unifiedId === newestUnifiedId,
        ),
        lastViewedAppointmentIndex: appointments.findIndex(
          (appointment) => appointment.appointment.unifiedId === unifiedId,
        ),
      };
    }, [appointments, nextAppointmentId, newestUnifiedId, unifiedId]);

  // handlers
  const scrollNextAppointmentIntoView = useCallback(() => {
    if (nextAppointmentIndex >= 0) {
      rowVirtualizer.scrollToIndex(nextAppointmentIndex);
    }
  }, [nextAppointmentIndex, rowVirtualizer]);

  const scrollNewAnalysisIntoView = useCallback(() => {
    if (newAnalysisIndex >= 0) {
      rowVirtualizer.scrollToIndex(newAnalysisIndex);
    }
  }, [newAnalysisIndex, rowVirtualizer]);

  const handleClickPreviousAppt = useCallback(
    (currentAppointment: { unifiedId: string; estimatedVisitId?: number }) => {
      const index = appointments.findIndex(
        (appointment) =>
          appointment.appointment.unifiedId === currentAppointment.unifiedId,
      );
      if (index > 0) {
        const newIndex = index - 1;
        const newAppt = appointments[newIndex];
        trackEvent(mixpanelEvents.huddleSheetPreviousClick, {
          unifiedId: newAppt?.appointment.unifiedId,
          estimatedVisitId: newAppt?.appointment.estimatedVisitId,
          imagingPatientId: newAppt?.patient?.imagingPatientId,
          pmsPatientId: newAppt?.patient?.pmsPatientId,
          pmsAppointmentId: newAppt?.appointment?.pmsAppointmentId,
        });
        setSelectedAppointment({
          unifiedId: newAppt.appointment.unifiedId,
          estimatedVisitId: newAppt.appointment.estimatedVisitId ?? 0,
        });
      }
    },
    [appointments, trackEvent],
  );

  const handleClickNextAppt = useCallback(
    (currentAppointment: { unifiedId: string; estimatedVisitId?: number }) => {
      const index = appointments.findIndex(
        (appointment) =>
          appointment.appointment.unifiedId === currentAppointment.unifiedId,
      );
      if (index < appointments.length - 1) {
        const newIndex = index + 1;
        const newAppt = appointments[newIndex];
        trackEvent(mixpanelEvents.huddleSheetNextClick, {
          unifiedId: newAppt?.appointment.unifiedId,
          estimatedVisitId: newAppt?.appointment.estimatedVisitId,
          imagingPatientId: newAppt?.patient?.imagingPatientId,
          pmsPatientId: newAppt?.patient?.pmsPatientId,
          pmsAppointmentId: newAppt?.appointment?.pmsAppointmentId,
        });
        setSelectedAppointment({
          unifiedId: newAppt.appointment.unifiedId,
          estimatedVisitId: newAppt.appointment.estimatedVisitId ?? 0,
        });
      }
    },
    [appointments, trackEvent],
  );

  const handleRefresh = useCallback(() => {
    refetch();
    setIsRefreshRequested(true);
    setTimeout(() => {
      setIsRefreshRequested(false);
    }, 1000);
  }, [refetch]);

  const handleShowNewAnalyses = useCallback(() => {
    if (canJumpToNewAnalyses) {
      scrollNewAnalysisIntoView();
    }
    setHasNewAnalyses(false);
  }, [canJumpToNewAnalyses, scrollNewAnalysisIntoView, setHasNewAnalyses]);

  const handleOpenSheet = useCallback(
    (appointment: Appointment, source: 'PATIENT_INFO' | 'ICON') => {
      trackEvent(mixpanelEvents.huddleTreatmentDetailsClick, {
        unifiedId: appointment.appointment.unifiedId,
        estimatedVisitId: appointment.appointment.estimatedVisitId,
        imagingPatientId: appointment?.patient?.imagingPatientId,
        source,
        pmsPatientId: appointment?.patient.pmsPatientId,
        pmsAppointmentId: appointment?.appointment.pmsAppointmentId,
      });
      setSelectedAppointment({
        unifiedId: appointment.appointment.unifiedId,
        estimatedVisitId: appointment.appointment.estimatedVisitId ?? 0,
      });
    },
    [trackEvent],
  );

  useEffect(() => {
    const newOperatoriesList = [
      ...new Set(
        (allAppointments ?? [])
          .map(({ appointment }) => appointment.operatory?.name)
          .filter(Boolean),
      ),
    ];

    setOperatoryList(newOperatoriesList as string[]);
  }, [allAppointments, setOperatoryList]);

  // Default to review tab if tabs are hidden
  useEffect(() => {
    if (shouldHideHuddle) {
      setSelectedTab('REVIEW');
      return;
    }
    // Only want to run this on first render w/ huddle data available
  }, [setSelectedTab, shouldHideHuddle, selectedTab]);

  useEffect(() => {
    if (selectedDate && practiceName) {
      document.title = `VideaAI ${format(selectedDate, 'MMMM d')} ${
        practiceName.length > 10
          ? practiceName.slice(0, 10) + '...'
          : practiceName
      } `;
    }
  }, [selectedDate, practiceName]);

  useEffect(() => {
    if (
      jumpToCurrentAppt === 'true' &&
      !isLoadingAppointments &&
      !isSalesDemo
    ) {
      scrollNextAppointmentIntoView();
    }
  }, [
    jumpToCurrentAppt,
    isLoadingAppointments,
    isSalesDemo,
    scrollNextAppointmentIntoView,
  ]);

  useEffect(() => {
    // Scroll to the appointment that was last viewed in the annotator.
    if (
      unifiedId &&
      !isLoadingAppointments &&
      lastViewedAppointmentIndex >= 0 &&
      lastViewedAppointmentIndex < appointments.length
    ) {
      rowVirtualizer.scrollToIndex(lastViewedAppointmentIndex, {
        align: 'center',
      });
    }
  }, [
    isLoadingAppointments,
    unifiedId,
    lastViewedAppointmentIndex,
    rowVirtualizer,
    appointments.length,
  ]);

  useEffect(() => {
    if (
      isBottomAppointmentInView &&
      nextCursor &&
      appointmentData?.pages.length &&
      appointmentData?.pages.length <= 20
    ) {
      fetchNextPage();
    }
  }, [
    isBottomAppointmentInView,
    fetchNextPage,
    nextCursor,
    appointmentData?.pages.length,
  ]);

  useEffect(() => {
    // Update params to match selected date
    if (selectedDate) {
      searchParams.set('selectedDateParam', format(selectedDate, 'yyyy-MM-dd'));
    } else {
      searchParams.delete('selectedDateParam');
    }

    // If we have new params, update history
    const newSearch = searchParams.toString();
    if (history.location.search !== newSearch) {
      const newUrl = `${history.location.pathname}${
        newSearch ? `?${newSearch}` : ''
      }`;
      const currentUrl = `${history.location.pathname}${history.location.search}`;

      // Only push if the complete URL is actually different
      if (newUrl !== currentUrl) {
        history.push({
          pathname: history.location.pathname,
          search: newSearch,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, history]);

  useEffect(() => {
    const storedView = sessionStorage.getItem('jotai-mode');
    const selectedDateParam = searchParams.get('selectedDateParam');

    // Default to selected date from params
    if (
      selectedDateParam &&
      isValid(new Date(selectedDateParam)) &&
      (selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null) !==
        format(new Date(selectedDateParam), 'yyyy-MM-dd')
    ) {
      selectDate(createLocalDate(selectedDateParam));
      // Otherwise select today
    } else if (
      (!storedView || storedView === 'RECENT_VISITS') &&
      !selectedDate
    ) {
      selectDate(today);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectDate, today, searchParams]);

  if (isLoadingPreferences) {
    return null;
  }

  return (
    <PatientListPageLayout
      context={PatientListPageLayoutContextMap.Huddle}
      allAppointments={appointments}
      appointmentIdsToTasks={appointmentIdsToTasks}
      filteredAppointments={appointmentsFilteredByProviderAndOperatory}
      nextAppointmentIndex={nextAppointmentIndex}
      parentRef={parentRef}
      providersList={providersList}
      taskCounts={taskCounts}
      visitSummaries={visitSummariesDeduplicated}
      hasNewAnalyses={hasNewAnalyses}
      isLoadingAppointments={isLoadingAppointments}
      isLoadingVisitSummaries={visitSummariesData[0]?.isLoading}
      isNextAppointmentInView={isNextAppointmentInView}
      isRefreshRequested={isRefreshRequested}
      onRefresh={handleRefresh}
      onShowNewAnalyses={handleShowNewAnalyses}
      onScrollNextAppointmentIntoView={scrollNextAppointmentIntoView}
    >
      <div
        className={patientListContainerClasses}
        data-testid="patient-list"
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
        }}
      >
        <div className={patientListClasses}>
          {rowVirtualizer.getVirtualItems().map((virtualRow, i) => {
            const appointment = appointments[virtualRow.index];
            const currentImagesDate = appointment.appointment.imagesAt;
            const isRecentAnalysis =
              !!currentImagesDate &&
              isWithinInterval(new Date(currentImagesDate), {
                start: sub(new Date(), { minutes: 15 }),
                end: new Date(),
              });
            const visitSummary = visitSummariesDeduplicated?.find((summary) => {
              return summary?.unifiedAppointmentId &&
                appointment.appointment.unifiedId
                ? summary.unifiedAppointmentId ===
                    parseInt(appointment.appointment.unifiedId)
                : summary?.estimatedVisitId &&
                    summary.estimatedVisitId ===
                      appointment.appointment.estimatedVisitId;
            });
            const fmxSummary = fmxSummaries?.find(
              (summary) =>
                summary?.estimatedVisitId ===
                appointment.appointment.estimatedVisitId,
            );
            const treatmentCounts = bulkTreatmentCounts?.find(
              (counts?: TTreatmentCounts) => {
                if (
                  counts?.unifiedAppointmentId ===
                  appointment.appointment.unifiedId
                ) {
                  return counts;
                }
              },
            );
            const zuubInsurance = zuubInsuranceByAppointmentId.get(
              appointment.appointment.unifiedId,
            );

            return (
              <div
                data-testid="patient-strip"
                className={`patient-strip-container ${
                  isRecentAnalysis && !appointment.viewed
                    ? 'newest-analysis'
                    : ''
                }`}
                key={`${isScheduleViewEnabled ? 'huddle' : 'review'} ${
                  appointment.appointment.unifiedId
                } ${mode}`}
                style={{
                  position: 'relative',
                  height: `fit-content`,
                }}
              >
                <div
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    minHeight: 'fit-content',
                    maxHeight: 'fit-content',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <PatientInfoSheet
                    appointment={
                      appointments.find(
                        (appt) =>
                          appt.appointment.unifiedId ===
                          selectedAppointment.unifiedId,
                      ) || appointment
                    }
                    visitSummaries={visitSummariesDeduplicated}
                    index={i}
                    onClickPreviousAppt={handleClickPreviousAppt}
                    onClickNextAppt={handleClickNextAppt}
                    selectedAppointment={selectedAppointment}
                    patientTask={
                      appointmentIdsToTasks[selectedAppointment.unifiedId]
                    }
                  >
                    <PatientStrip
                      appointment={appointment}
                      fmxSummary={fmxSummary}
                      visitSummary={visitSummary}
                      index={i}
                      patientTask={
                        appointmentIdsToTasks[appointment.appointment.unifiedId]
                      }
                      treatmentCounts={treatmentCounts}
                      zuubInsurance={zuubInsurance}
                      lastViewedAppointmentRef={
                        appointment.appointment.unifiedId === unifiedId
                          ? lastViewedAppointmentRef
                          : undefined
                      }
                      onOpenSheet={handleOpenSheet}
                      isLoadingVisitSummaries={visitSummariesData[0]?.isLoading}
                    />
                  </PatientInfoSheet>
                </div>
              </div>
            );
          })}
        </div>
        {/* Sentinel element for infinite scroll detection */}
        <div ref={bottomRef} style={{ height: '1px', width: '100%' }} />
        {isFetchingNextPage && appointments?.length !== 0 && (
          <Styled.LoadingSpinner className="infinite-load-spinner" />
        )}
        <PdfViewerV2 />
        <ClaimSummary />
      </div>
    </PatientListPageLayout>
  );
};
